import {Map, List, fromJS} from 'immutable';
import {ActionTypes} from './Actions';
import _ from 'lodash';

const initialState = {
  groupHeaders: List(),
  selectedGroupHeader: null,

  /**
   * Features from current group from current page
   */
  groupFeatures: List(),

  selectedFeaturesGroups: {},
  /** Selected features within a group, regardless of the current page
   * {groupId => {featureId => feature}}
   */
  selectedFeatures: {},

  page: 0,
  totalPages: 1,
  itemsPerPage: '50',
  term: '',
  groupsTerm: '',

  areFeaturesFiltersCollapsed: true,

  featuresFiltersToBeDeleted: Map(),

  colorFilters: [],
  allOtherFilters: [],
  currentFilterValues: []
};

const __setFeaturesGroupHeaders = (state, {payload}) => {
  let selectedGroupHeader = null;
  const {headers, filters} = payload;
  if (headers && headers.length) {
    selectedGroupHeader = headers[0];
  }
  const colorFilters = _.orderBy(_.find(filters, {name: 'Color'}).values, c => c.displayText || c.value);
  const allOtherFilters = _.orderBy(_.filter(filters, fg => fg.name !== 'Color'), f => f.name);
  _.forEach(allOtherFilters, af => af.values = _.orderBy(af.values, c => c.displayText || c.value));
  return {
    ...state, groupHeaders: fromJS(headers), selectedGroupHeader,
    selectedFeatures: {},
    colorFilters,
    allOtherFilters
  };
}

const __setFeaturesFromGroup = (state, {payload}) => {
  const {selectedGroupHeader, selectedFeatures} = state;
  const selectedFeaturesFromGroup = selectedFeatures[selectedGroupHeader.id];

  let groupFeatures = fromJS(payload.rows);

  selectedFeaturesFromGroup && _.forEach(selectedFeaturesFromGroup, (checked, featureId) => {
    const idx = groupFeatures.findIndex(gf => gf.get('id') === +featureId);
    if (idx !== -1) {
      groupFeatures = groupFeatures.setIn([idx, 'checked'], true);
    }
  });

  return {...state, groupFeatures, page: payload.page, totalPages: payload.totalPages};
}

const __markFeatureChecked = (state, {payload}) => {
  const {feature, check} = payload;

  const {selectedFeatures, selectedGroupHeader} = state;
  if (!selectedFeatures[selectedGroupHeader.id])
    selectedFeatures[selectedGroupHeader.id] = {};

  if (check)
    selectedFeatures[selectedGroupHeader.id][feature.id] = feature;
  else
    delete selectedFeatures[selectedGroupHeader.id][feature.id];

  let groupFeatures = state.groupFeatures;
  const idx = groupFeatures.findIndex(gf => gf.get('id') === feature.id);
  groupFeatures = groupFeatures.setIn([idx, 'checked'], check);
  return {...state, groupFeatures, selectedFeatures};
}

const __markAllFeaturesChecked = (state, {payload}) => {
  let {selectedGroupHeader, selectedFeatures, groupFeatures} = state;
  let selectedFeaturesFromGroup = selectedFeatures[selectedGroupHeader.id];
  if (!selectedFeaturesFromGroup)
    selectedFeaturesFromGroup = {};

  groupFeatures = groupFeatures.map(item => {
    item = item.set('checked', payload);
    if (payload)
      selectedFeaturesFromGroup[+item.get('id')] = item.toJS();
    else
      delete selectedFeaturesFromGroup[+item.get('id')];
    return item;
  });

  if (!payload) {
    selectedFeatures = {};
  } else {
    selectedFeatures[selectedGroupHeader.id] = selectedFeaturesFromGroup;
  }

  return {...state, groupFeatures, selectedFeatures};
}

const __collapseFeatureFilters = (state, {collapse}) => {
  return {...state, areFeaturesFiltersCollapsed: collapse};
}

const __markFeatureFilterForDeletion = (state, {featureId, filterValueId, mark}) => {
  let {featuresFiltersToBeDeleted} = state;
  if (mark) {
    featuresFiltersToBeDeleted = featuresFiltersToBeDeleted.set(`${featureId}_${filterValueId}`, {
      featureId,
      filterValueId
    });
  } else {
    featuresFiltersToBeDeleted = featuresFiltersToBeDeleted.delete(`${featureId}_${filterValueId}`);
  }

  return {...state, featuresFiltersToBeDeleted};
}

const __selectGroup = (state, {group, select}) => {
  const {selectedFeaturesGroups} = state;
  if (select) {
    if (_.size(selectedFeaturesGroups) >= 5)
      return state;
    selectedFeaturesGroups[group.id] = true;
  } else {
    delete selectedFeaturesGroups[group.id];
  }

  return {...state, selectedFeaturesGroups, page: 0, term: ''};
}

const __addFilterItem = (state, {item}) => {
  const {currentFilterValues} = state;
  if (item.checked) {
    currentFilterValues.push(item.id);
  } else {
    _.remove(currentFilterValues, i => i === item.id);
  }

  return {...state, currentFilterValues};
}

export default (state = initialState, action) => {
  switch (action.type) {
    case ActionTypes.GET_FEATURES_GROUP_HEADERS_LOADING:
      return {...state};
    case ActionTypes.GET_FEATURES_GROUP_HEADERS_LOADED:
      return __setFeaturesGroupHeaders(state, action);
    case ActionTypes.GET_FEATURES_FROM_GROUP_LOADING:
      return {...state, selectedGroupHeader: action.payload};
    case ActionTypes.GET_FEATURES_FROM_GROUP_LOADED:
      return __setFeaturesFromGroup(state, action);
    case ActionTypes.MARK_FEATURE_CHECKED:
      return __markFeatureChecked(state, action);
    case ActionTypes.MARK_ALL_FEATURES_CHECKED:
      return __markAllFeaturesChecked(state, action);
    case ActionTypes.RESET_TERM:
      return {
        groupHeaders: List(),
        selectedGroupHeader: null,
        groupFeatures: List(),
        page: 0,
        totalPages: 1,
        term: '',
        selectedFeatures: {}
      };
    case ActionTypes.COLLAPSE_FEATURES_FILTERS:
      return __collapseFeatureFilters(state, action.payload);
    case ActionTypes.MARK_FEATURE_FILTER_FOR_DELETION:
      return __markFeatureFilterForDeletion(state, action.payload);
    case ActionTypes.CANCEL_FEATURE_FILTER_DELETION:
      return {...state, featuresFiltersToBeDeleted: Map()};
    case ActionTypes.FILTER_GROUPS_NAMES:
      return {...state, groupsTerm: action.payload.groupsTerm};
    case ActionTypes.SELECT_FEATURES_GROUP:
      return __selectGroup(state, action.payload);
    case ActionTypes.SET_PAGE:
      return {...state, page: action.payload.page};
    case ActionTypes.SET_TERM:
      return {...state, term: action.payload.term, page: 0};
    case ActionTypes.SET_ITEMS_PER_PAGE:
      return {...state, itemsPerPage: action.payload.itemsPerPage, page: 0};
    case ActionTypes.HANDLE_COLOR_FILTER_CLICK:
    case ActionTypes.HANDLE_NON_COLOR_FILTER_CLICK:
      return __addFilterItem(state, action.payload);
  }
  return state;
}

export const getCurrentGroupCheckedFeatures = (state) => {
  const {groupFeatures, selectedGroupHeader, selectedFeatures} = state;

  const selectedFeaturesFromGroup = selectedGroupHeader ? selectedFeatures[selectedGroupHeader.id] : null;
  if (!selectedFeaturesFromGroup)
    return [];
  return _.values(selectedFeaturesFromGroup);
}

export const isFeatureFilterMarkedForDeletion = (state, featureId, filterValueId) => {
  return state.featuresFiltersToBeDeleted.has(`${featureId}_${filterValueId}`);
}
